import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import AuthLayout from './AuthLayout';
import { supabase } from '../../config/supabase';
import GoogleButton from "react-google-button";
import { toast } from 'react-hot-toast';

const validateForm = (formData) => {
  const errors = {};

  if (formData.password !== formData.confirmPassword) {
    errors.confirmPassword = 'Passwords do not match';
  }

  if (formData.password.length < 8) {
    errors.password = 'Password must be at least 8 characters long';
  }

  // Password must contain at least one uppercase, one lowercase, one number, and one special character
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])/;
  if (!passwordRegex.test(formData.password)) {
    errors.password = 'Password must include uppercase, lowercase, number, and special character';
  }

  if (!formData.email.includes('@')) {
    errors.email = 'Please enter a valid email address';
  }

  // General phone number validation for international format
  const phoneRegex = /^\+?[1-9]\d{1,14}$/;
  if (!phoneRegex.test(formData.phone_number)) {
    errors.phone_number = 'Please enter a valid phone number';
  }

  // Basic URL validation
  if (formData.business_website && !formData.business_website.match(/^https?:\/\/.+\..+/)) {
    errors.business_website = 'Please enter a valid website URL starting with http:// or https://';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

export default function SignUp() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone_number: '',
    country: 'UAE',
    company_name: '',
    business_website: '',
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [countries, setCountries] = useState([]);
  const { signUp, signUpWithGoogle, loading } = useAuth();

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await fetch('https://restcountries.com/v3.1/all');
        const data = await response.json();
        const countryList = data.map(country => ({
          value: country.cca3, // Use the 3-letter country code
          label: country.name.common,
        }));
        setCountries(countryList);
      } catch (error) {
        console.error('Error fetching countries:', error);
      }
    };

    fetchCountries();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Create Account button clicked'); // Debug log

    const { isValid, errors } = validateForm(formData);
    if (!isValid) {
      setFormErrors(errors);
      return;
    }

    try {
      const { confirmPassword, ...signUpData } = formData;
      console.log(signUpData)
      await signUp(signUpData);
    } catch (error) {
      setFormErrors(prev => ({
        ...prev,
        submit: error.message
      }));
    }
  };

  const getInputClassName = (fieldName) => `
    block w-full appearance-none rounded-md border 
    ${formErrors[fieldName] ? 'border-red-300' : 'border-gray-300'} 
    px-3 py-2 placeholder-gray-400 shadow-sm 
    focus:border-[#0049ac] focus:outline-none focus:ring-[#0049ac] 
    sm:text-sm
  `;

  return (
    <AuthLayout
      title="Create your account"
      subtitle={
        <>
          Already have an account?{' '}
          <Link to="/signin" className="font-medium text-[#0049ac] hover:text-[#0049ac]/90">
            Sign in
          </Link>
        </>
      }
    >
      <form className="space-y-6" onSubmit={handleSubmit}>
        {formErrors.submit && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">{formErrors.submit}</h3>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
              First name
            </label>
            <div className="mt-1">
              <input
                type="text"
                name="firstName"
                id="firstName"
                required
                value={formData.firstName}
                onChange={handleChange}
                className={getInputClassName('firstName')}
              />
              {formErrors.firstName && (
                <p className="mt-1 text-sm text-red-600">{formErrors.firstName}</p>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
              Last name
            </label>
            <div className="mt-1">
              <input
                type="text"
                name="lastName"
                id="lastName"
                required
                value={formData.lastName}
                onChange={handleChange}
                className={getInputClassName('lastName')}
              />
              {formErrors.lastName && (
                <p className="mt-1 text-sm text-red-600">{formErrors.lastName}</p>
              )}
            </div>
          </div>
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            Email address
          </label>
          <div className="mt-1">
            <input
              type="email"
              name="email"
              id="email"
              required
              value={formData.email}
              onChange={handleChange}
              className={getInputClassName('email')}
            />
            {formErrors.email && (
              <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="phone_number" className="block text-sm font-medium text-gray-700">
            Phone number
          </label>
          <div className="mt-1">
            <input
              type="tel"
              name="phone_number"
              id="phone_number"
              required
              placeholder="+971501234567"
              value={formData.phone_number}
              onChange={handleChange}
              className={getInputClassName('phone_number')}
            />
            {formErrors.phone_number && (
              <p className="mt-1 text-sm text-red-600">{formErrors.phone_number}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="company_name" className="block text-sm font-medium text-gray-700">
            Company Name
          </label>
          <div className="mt-1">
            <input
              type="text"
              name="company_name"
              id="company_name"
              required
              value={formData.company_name}
              onChange={handleChange}
              className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-[#0049ac] focus:outline-none focus:ring-[#0049ac] sm:text-sm"
            />
            {formErrors.company_name && (
              <p className="mt-1 text-sm text-red-600">{formErrors.company_name}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="business_website" className="block text-sm font-medium text-gray-700">
            Business Website
          </label>
          <div className="mt-1">
            <input
              type="url"
              name="business_website"
              id="business_website"
              required
              placeholder="https://example.com"
              value={formData.business_website}
              onChange={handleChange}
              className={getInputClassName('business_website')}
            />
            {formErrors.business_website && (
              <p className="mt-1 text-sm text-red-600">{formErrors.business_website}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="country" className="block text-sm font-medium text-gray-700">
            Country
          </label>
          <div className="mt-1">
            <select
              id="country"
              name="country"
              required
              value={formData.country}
              onChange={handleChange}
              className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-[#0049ac] focus:outline-none focus:ring-[#0049ac] sm:text-sm"
            >
              {countries.map(country => (
                <option key={country.value} value={country.value}>
                  {country.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700">
            Password
          </label>
          <div className="mt-1">
            <input
              type="password"
              name="password"
              id="password"
              required
              value={formData.password}
              onChange={handleChange}
              className={getInputClassName('password')}
            />
            {formErrors.password && (
              <p className="mt-1 text-sm text-red-600">{formErrors.password}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
            Confirm Password
          </label>
          <div className="mt-1">
            <input
              type="password"
              name="confirmPassword"
              id="confirmPassword"
              required
              value={formData.confirmPassword}
              onChange={handleChange}
              className={getInputClassName('confirmPassword')}
            />
            {formErrors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600">{formErrors.confirmPassword}</p>
            )}
          </div>
        </div>

        <div>
          <button
            type="submit"
            disabled={loading}
            className="flex w-full justify-center rounded-md border border-transparent bg-[#0049ac] py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-[#0049ac]/90 focus:outline-none focus:ring-2 focus:ring-[#0049ac] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating account...' : 'Create account'}
          </button>
        </div>
        <div className='flex justify-center'>
        <GoogleButton onClick={signUpWithGoogle} />
          {/* <button
            type="button"
            onClick={signUpWithGoogle}
            className="flex w-full justify-center rounded-md border border-transparent bg-red-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2"
          >
            Sign up with Google
          </button> */}
        </div>
      </form>
    </AuthLayout>
  );
} 